import { motion } from "framer-motion";
import { Shield, Lock, Eye, Server, ArrowRight, ShieldCheck, FileLock2, Zap } from "lucide-react";
import SEO from "../components/SEO";

export default function Security() {
    const securityFeatures = [
        {
            icon: <Lock className="w-6 h-6 text-blue-500" />,
            title: "Bank-Grade Encryption",
            desc: "All data is encrypted in transit and at rest using AES-256 and TLS 1.3 protocol. Your sensitive financial data is never stored in plain text."
        },
        {
            icon: <Eye className="w-6 h-6 text-emerald-500" />,
            title: "Proactive Monitoring",
            desc: "Our AI-driven security engine monitors for suspicious activities 24/7, automatically blocking potential threats before they reach your data."
        },
        {
            icon: <Server className="w-6 h-6 text-purple-500" />,
            title: "99.99% Uptime",
            desc: "Hosted on globally distributed, redundant cloud infrastructure to ensure your business operations never miss a beat."
        },
        {
            icon: <ShieldCheck className="w-6 h-6 text-amber-500" />,
            title: "Compliance Ready",
            desc: "We adhere to SOC2, GDPR, and ISO 27001 standards, ensuring that your business meets international data protection regulations."
        },
        {
            icon: <FileLock2 className="w-6 h-6 text-rose-500" />,
            title: "Multi-Factor Auth",
            desc: "Protect your account with mandatory MFA. Support for biometric, hardware keys, and TOTP authentication methods."
        },
        {
            icon: <Zap className="w-6 h-6 text-cyan-500" />,
            title: "Instant Backups",
            desc: "Point-in-time recovery and real-time data replication ensure that your business continuity is guaranteed under any circumstance."
        }
    ];

    return (
        <div className="min-h-screen bg-base text-primary transition-colors duration-300">
            <SEO
                title="Security & Trust"
                description="Learn how blinqbooks protects your business data. Enterprise-grade encryption, proactive monitoring, and global compliance."
            />

            {/* Hero Section */}
            <section className="relative py-20 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center max-w-3xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase mb-6"
                        >
                            <Shield className="w-4 h-4" />
                            Enterprise Grade Security
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight"
                        >
                            Your Data is Our <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-500">Highest Priority</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-lg text-secondary dark:text-gray-300 mb-8 leading-relaxed font-medium"
                        >
                            We employ multi-layered security protocols to ensure your business stays protected, compliant, and always online.
                        </motion.p>
                    </div>
                </div>

                {/* Ambient background decoration */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] -mr-64 -mt-64"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[120px] -ml-64 -mb-64"></div>
            </section>

            {/* Features Grid */}
            <section className="py-24 relative">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {securityFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="p-8 rounded-2xl bg-slate-500/[0.02] dark:bg-white/[0.01] hover:bg-blue-500/[0.06] transition-all duration-300 group"
                            >
                                <div className="p-3 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 w-fit mb-6 group-hover:scale-110 transition-transform">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-4 text-primary dark:text-white">{feature.title}</h3>
                                <p className="text-secondary dark:text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Certifications / Trust Indicators */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h2 className="text-2xl font-bold mb-12 text-primary dark:text-white">Built on industry standards</h2>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 dark:opacity-40 grayscale">
                        {['SOC2 Type II', 'GDPR Compliant', 'ISO 27001', 'PCI DSS Level 1', 'HIPAA Ready'].map((cert) => (
                            <span key={cert} className="text-xl font-bold text-gray-500 dark:text-gray-400">{cert}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-primary dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Peace of mind guaranteed</h2>
                    <p className="text-blue-100 mb-8 text-lg relative z-10">Host your business data on the most secure infrastructure available.</p>
                    <button className="px-10 py-4 bg-white text-primary dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        View Security Whitepaper <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
